import { gql, useQuery } from "@apollo/client";
import { createContext, FC, useCallback, useContext, useReducer } from "react";
import { User } from "../api/schema";

export const me_query = gql`
  query Me {
    me {
      id
      displayname
      username
      email
      roles {
        id
        name
        permissions {
          id
          node
        }
      }
      permissions {
        id
        node
      }
    }
  }
`;

interface MeQueryReponse {
  me: User | null;
}

export interface ProfileState {
  loading: boolean;
  user: User | null;
}

const _default_state: ProfileState = { loading: true, user: null };
export const MeRepositoryContext = createContext<ProfileState>(_default_state);

const RefetchProfileContext = createContext<() => void>(() => {});

export const MeRepositoryProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(
    (old_value: ProfileState, new_value: ProfileState) => ({
      ...old_value,
      ...new_value,
    }),
    _default_state
  );

  const { refetch } = useQuery<MeQueryReponse>(me_query, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      // console.log("got profile data", data);
      if (data.me) dispatch({ user: data.me, loading: false });
      else dispatch({ user: null, loading: false });
    },
    onError: (error) => {
      // console.log("profile data error", error);
      dispatch({ user: null, loading: false });
    },
  });

  const fetch_new_data = useCallback(() => {
    // console.log("refetching...");
    dispatch(_default_state);
    refetch().then((v) => {
      if (v.error) {
        console.error(v.error);
      } else if (v.loading) {
        // console.log("loading...");
      } else if (v.data) {
        // console.log("setting new data");
        dispatch({ loading: false, user: v.data.me });
      }
    });
  }, [dispatch, refetch]);

  return (
    <MeRepositoryContext.Provider value={state}>
      <RefetchProfileContext.Provider value={fetch_new_data}>
        {children}
      </RefetchProfileContext.Provider>
    </MeRepositoryContext.Provider>
  );
};

export const useApiUser = () => ({
  profile: useContext(MeRepositoryContext),
  refetch: useContext(RefetchProfileContext),
});
